/**
 * Chat Utility Helper Functions
 *
 * Pure utility functions extracted from Chat.tsx for better maintainability and testability.
 * These functions have no side effects and can be easily unit tested.
 */

import { AppType } from "../../../api";
import { v4 as uuidv4 } from "uuid";

/**
 * Gets the app types to check based on the current app type
 * @param appType - Current app type (ALL, FAQ, or Manuals)
 * @returns Array of app types to check for question ID
 */
export const getAppTypesToCheck = (appType: AppType): AppType[] => {
    const appTypesToCheck: Record<AppType, AppType[]> = {
        ALL: ["FAQ", "Manuals"],
        FAQ: ["ALL", "Manuals"],
        Manuals: ["ALL", "FAQ"]
    };
    return appTypesToCheck[appType] || [];
};

/**
 * Gets the app name based on app type
 * @param appType - Current app type
 * @returns Normalized app name
 */
export const getAppName = (appType: AppType): string => {
    return appType === "ALL" ? "ALL" : appType === "FAQ" ? "Manuals" : "FAQ";
};

/**
 * Finds question ID and has_question_id from answer object
 * @param answer - Answer object containing responses
 * @param appType - Current app type
 * @returns Object with question_id and has_question_id
 */
export const findQuestionIdFromAnswer = (answer: any, appType: AppType): { question_id?: string; has_question_id?: boolean; original_question_id?: string } => {
    const appName = getAppName(appType);
    let question_id: string | undefined;
    let has_question_id: boolean | undefined;

    const typesToCheck = getAppTypesToCheck(appType);
    for (const typeToCheck of typesToCheck) {
        const questionId = answer?.[typeToCheck]?.context?.question_id;
        const hasQuestionId = answer?.[typeToCheck]?.context?.has_question_id;

        if (questionId && hasQuestionId) {
            question_id = questionId;
            has_question_id = hasQuestionId;
            break;
        }
    }

    const original_question_id = question_id || answer?.[appName]?.context?.question_id;

    return { question_id, has_question_id, original_question_id };
};

/**
 * Generates question ID for new questions or edits
 * @param questionIdData - Object containing existing question ID info
 * @param isEdit - Whether this is an edit operation
 * @returns Object with question_id and has_question_id
 */
export const generateQuestionId = (
    questionIdData: { question_id?: string; has_question_id?: boolean },
    isEdit: boolean
): { question_id: string; has_question_id: boolean } => {
    if (questionIdData.question_id === undefined || questionIdData.has_question_id === undefined || isEdit) {
        return {
            question_id: String(uuidv4()),
            has_question_id: true
        };
    }
    return {
        question_id: questionIdData.question_id,
        has_question_id: questionIdData.has_question_id
    };
};

/**
 * Gets conversation ID from answer object
 * @param answer - Answer object
 * @param appType - Current app type
 * @returns Conversation ID or undefined
 */
export const getConvIdFromAnswer = (answer: any, appType: AppType): string | undefined => {
    const appName = getAppName(appType);
    return answer?.[appName]?.context?.conv_id;
};

/**
 * Prepares question with template instructions if applicable
 * @param question - Base question text
 * @param extractedQuestion - Extracted question text
 * @param templateId - Template ID
 * @param templateContents - Array of template contents
 * @param isEdit - Whether this is an edit operation
 * @param getPromptInstructionsToRender - Function to render prompt instructions
 * @returns Prepared question text
 */
export const prepareQuestionWithTemplate = (
    question: string,
    extractedQuestion: string,
    templateId: string | undefined,
    templateContents: any[],
    isEdit: boolean,
    getPromptInstructionsToRender: (template: any) => string
): string => {
    if (isEdit && !!(templateId && templateId !== "") && templateId !== "None") {
        const filteredTemplateValue = templateContents
            ?.filter((tempContent: any) => tempContent.id === templateId)
            .map((tempContent: any) => tempContent.template[0])[0];
        return extractedQuestion + getPromptInstructionsToRender(filteredTemplateValue);
    }
    return question;
};

/**
 * Checks if combined app type response is needed
 * @param combinedAppTypeResponse - Explicit combined response flag
 * @param answer - Answer object
 * @param isEdit - Whether this is an edit operation
 * @returns Whether combined app type response should be used
 */
export const shouldUseCombinedAppTypeResponse = (combinedAppTypeResponse: boolean, answer: any, isEdit: boolean): boolean => {
    return combinedAppTypeResponse || (!!(answer && !!(answer["ALL"] != null && answer["ALL"] !== undefined)) && !combinedAppTypeResponse && !isEdit);
};

/**
 * Generates a new session ID
 * @returns New UUID session ID
 */
export const generateNewSessionId = (): string => {
    return String(uuidv4());
};

/**
 * Checks if file attachments are present
 * @param fileAttachments - Array of file attachments
 * @returns Boolean indicating if files are attached
 */
export const hasFileAttachments = (fileAttachments?: any[]): boolean => {
    return !!(fileAttachments && fileAttachments.length > 0);
};

/**
 * Normalizes file attachments array
 * @param fileAttachments - File attachments or undefined
 * @returns Normalized array (empty if undefined)
 */
export const normalizeFileAttachments = (fileAttachments?: any[]): any[] => {
    return fileAttachments || [];
};
